import { Img, Layout, Rect, Txt, brightness, invert, type Node } from "@revideo/2d";
import type { Clip } from "@hudhud/core";
import { COLORS, HEIGHT, WIDTH, LOGO_PAD, logoOpacityAt, logoWidth } from "@hudhud/reel/math";

/** Where each anchor puts the logo: the point on the canvas and which corner of the logo sits on it (Motion Canvas' offset). */
function anchorOf(anchor: Clip["brand"]["logo"]): { x: number; y: number; offset: [number, number] } {
  const PAD = LOGO_PAD;
  const X = (px: number) => px - WIDTH / 2, Y = (px: number) => px - HEIGHT / 2;
  switch (anchor) {
    case "tl": return { x: X(PAD), y: Y(PAD + 6), offset: [-1, -1] };
    case "tr": return { x: X(WIDTH - PAD), y: Y(PAD + 6), offset: [1, -1] };
    case "tc": return { x: 0, y: Y(PAD + 6), offset: [0, -1] };
    case "ml": return { x: X(PAD), y: 0, offset: [-1, 0] };
    case "mr": return { x: X(WIDTH - PAD), y: 0, offset: [1, 0] };
    case "bl": return { x: X(PAD), y: Y(HEIGHT - HEIGHT * 0.09), offset: [-1, 1] };
    case "br": return { x: X(WIDTH - PAD), y: Y(HEIGHT - HEIGHT * 0.09), offset: [1, 1] };
    default: return { x: 0, y: Y(HEIGHT - HEIGHT * 0.075), offset: [0, 1] };
  }
}

/** The logo over the video: anchored, nudged, fading in over the first three seconds, in one of the brand's styles. Mirrors Logo in the Remotion reel. */
export function logoLayer(o: { clip: Clip; logoUrl: string; frame: () => number; from: number; lite?: boolean }): Node | null {
  const b = o.clip.brand;
  if (b.logo === "off") return null;
  const w = logoWidth(b.logoScale);
  const a = anchorOf(b.logo);
  const shadow = o.lite ? {} :
    b.logoStyle === "mono" ? { shadowColor: "rgba(0,0,0,0.6)", shadowBlur: 12, shadowOffset: [0, 4] as [number, number] } :
    b.logoStyle === "glow" ? { shadowColor: "rgba(34,177,76,0.9)", shadowBlur: 22 } :
    { shadowColor: "rgba(0,0,0,0.6)", shadowBlur: 16, shadowOffset: [0, 6] as [number, number] };
  const filters = !o.lite && b.logoStyle === "mono" ? [brightness(0), invert(1)] : [];
  const img = new Img({ src: o.logoUrl, width: w, ...shadow, filters });
  const boxed = b.logoStyle === "pill" || b.logoStyle === "tag";
  const body: Node = boxed
    ? new Rect({
        layout: true, direction: "row", alignItems: "center", gap: 18 * b.logoScale, padding: [14 * b.logoScale, 24 * b.logoScale], radius: 999,
        fill: "rgba(11,15,13,0.55)", stroke: "rgba(255,255,255,0.14)", lineWidth: 1, shadowColor: "rgba(0,0,0,0.35)", shadowBlur: 30, shadowOffset: [0, 10],
        children: [new Img({ src: o.logoUrl, width: w }), ...(b.logoStyle === "tag" ? [new Txt({ text: o.clip.endCard.handle, fontFamily: '"Inter Tight", "Helvetica Neue", Arial, sans-serif', fontWeight: 700, fontSize: 34 * b.logoScale, fill: COLORS.offWhite, letterSpacing: -0.5 })] : [])],
      })
    : img;
  return new Layout({
    layout: true, x: a.x + b.logoX, y: a.y + b.logoY, offset: a.offset,
    opacity: () => logoOpacityAt(o.frame() - o.from, b.logoOpacity),
    children: [body],
  });
}
